<script setup lang="ts">
// import * as Sentry from "@sentry/vue";
import * as Sentry from "@sentry/vue";
import { useProductPageStore } from "~/stores/main";
import { useMeilisearch } from "~/utils/meilisearch";

/*
definePageMeta({
  middleware: [
    async function (to) {
      try {
        const masterProduct = await useMeilisearch(
          String(useRuntimeConfig().public.meilisearchIndexMasterproducts),
          {
            filter: ["sku = " + to.params.sku],
          },
        );
        if (Array.isArray(masterProduct) && masterProduct.length === 0) {
          throw createError({
            statusCode: 404,
          });
        }
      } catch (error) {
        Sentry.captureEvent({
          message: `An error occured when fetching master product (${to.params.sku})`,
          level: "error",
          extra: {
            error,
          },
        });
      }
    },
  ],
});
*/

// -----------------------
// composables
// -----------------------
const route = useRoute();
const config = useRuntimeConfig();
const store = useProductPageStore();
const { totalStockOfVariant, getVariantByProperty, createMetaDescription } =
  useHelpers();
const { productPageUrl } = useHelpers();

// -----------------------
// reactive properties
// -----------------------
const productFoundRef = ref<boolean>(true);

// -----------------------
// product load logic
// -----------------------
const masterProduct = await useMeilisearch(
  String(config.public.meilisearchIndexMasterproducts),
  {
    filter: ["sku = " + route.params.sku],
  },
);

if (Array.isArray(masterProduct) && masterProduct.length === 1) {
  store.masterProduct = masterProduct[0];
  const products = store.masterProduct.products;
  const productsInStock = products.filter(
    (variant) => totalStockOfVariant(variant) > 0,
  );
  let defaultColor = null;
  let defaultSize = null;

  if (products.length === 1) {
    defaultColor = products[0].product.product_szin_property;
    defaultSize = products[0].product.product_meret_property;
  } else if (productsInStock.length > 0) {
    defaultColor = productsInStock[0].product.product_szin_property;
  } else {
    defaultColor = products[0].product.product_szin_property;
  }

  // Select variant with the specified palette on load
  if ("color" in route.query) {
    const color = decodeURIComponent(route.query.color);
    const variantWithColor = getVariantByProperty(
      store.masterProduct,
      "product_szin_property",
      color,
    );

    if (variantWithColor) {
      defaultColor = color;
    }
  }

  if ("size" in route.query) {
    const size = decodeURIComponent(route.query.size);
    const variantWithSize = getVariantByProperty(
      store.masterProduct,
      "product_meret_property",
      size,
    );

    if (variantWithSize) {
      defaultSize = size;
    }
  }

  if (defaultSize === null) {
    const sizes = store.masterProduct.products
      .filter((product) => product.product.stock > 0) // only include products with stock
      .map((data: Record<any, any>) => data.product.product_meret_property) // Collect all sizes
      .filter(
        (size: string, i: number, self: string[]) => self.indexOf(size) === i,
      ); // Make the list unique

    if (sizes.length === 1) {
      defaultSize = sizes[0];
    }
  }

  if (defaultColor !== null) {
    store.color = defaultColor;
  }

  if (defaultSize !== null) {
    store.size = defaultSize;
  }

  // fetch manufacturer data for logo
  if (store.masterProduct?.masterproduct_gyarto_property) {
    store.manufacturer = await $fetch(`/api/meilisearch/manufacturer`, {
      params: { name: store.masterProduct.masterproduct_gyarto_property },
    });
  }

  const pageTitlePostfix =
    config.public.theme.group === "tnf" ? "The North Face Shop" : "Mountex";

  // -----------------------
  // Schema.org product data
  // -----------------------
  const soOffers = [];
  for (const soOffer of products) {
    soOffers.push({
      "@type": "Offer",
      price: soOffer.product.price,
      sku: soOffer.product.sku,
      availability:
        soOffer.product.stock > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
    });
  }
  const soProductData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: store.masterProduct?.full_name,
    sku: store.masterProduct?.sku,
    brand: {
      "@type": "Brand",
      name: store.masterProduct?.masterproduct_gyarto_property,
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: useRuntimeConfig().public.currency,
      lowPrice: store.masterProduct?.calculated_base_prices.price_min,
      highPrice: store.masterProduct?.calculated_base_prices.price_max,
      offerCount: soOffers.length,
      availability:
        store.masterProduct?.stock > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      offers: soOffers,
    },
  };

  useHead({
    title: `${store.masterProduct?.full_name} | ${pageTitlePostfix}`,
    meta: [
      {
        name: "description",
        content: createMetaDescription(store.masterProduct?.description),
      },
    ],
    bodyAttrs: {
      class: "product-page",
    },
    link: [
      {
        rel: "canonical",
        href: productPageUrl(store.masterProduct, false),
      },
    ],
  });

  useHead({
    script: [
      {
        type: "application/ld+json",
        children: JSON.stringify(soProductData),
      },
    ],
  });
} else if (Array.isArray(masterProduct) && masterProduct.length === 0) {
  throw createError({
    statusCode: 404,
  });
} else {
  Sentry.captureEvent({
    message: `An error occured when fetching master product`,
    level: "error",
    extra: {
      sku: route.params.sku,
    },
  });
}
</script>

<template>
  <NuxtErrorBoundary>
    <template #default>
      <div v-if="productFoundRef">
        <ProductPageBreadcrumb></ProductPageBreadcrumb>
        <div class="lg:px-5">
          <div
            class="product-container grid grid-cols-2 gap-x-[20px] items-start p-2 lg:p-5 rounded-2xl bg-brand-surface mx-auto max-w-[1880px]"
          >
            <ProductPageLeft></ProductPageLeft>
            <ProductPageRight></ProductPageRight>
          </div>
        </div>
      </div>
    </template>
  </NuxtErrorBoundary>
</template>
